import { Box, Button, Card, CardHeader, CircularProgress, Grid, Icon, IconButton, Toolbar, Tooltip, Typography } from '@material-ui/core';
import React, { useCallback, useContext, useState } from 'react';
import { DragDropContext, Draggable, DraggableProvided, Droppable, DropResult } from 'react-beautiful-dnd';
import { useParams } from 'react-router-dom';
import { ChildRoutes, SiteContext, StudentContext, UserContext } from '../../contexts';
import { Milestone, MilestoneGroup, Roadmap, RoadmapMilestone, RoadmapMilestoneGroup, RoadmapMilestoneState, Role, SuccessPath, User } from '../../dto';
import UserHelper, { MilestoneState } from '../../helpers/userHelper';
import { useAddMilestoneToPath, useDialogController, useRoadmapMilestones, useUpdateRoadmapMilestoneGroups, useUpdateRoadmapMilestones } from '../../hooks';
import useRoadmap from '../../hooks/gql/useRoadmap';
import useRoadmapMilestoneGroups from '../../hooks/gql/useRoadmapMilestoneGroups';
import { EmailDialog } from '../Common';
import { LanguageString } from '../Common/LanguageString';
import { FinalizeRoadmap } from './FinalizeRoadmap';
import { Goal } from './Goal';
import { MilestoneList, UserMilestone } from './MilestoneList';
import MTSInRoadmap from './MTSInRoadmap';
import RoadmapPrintDialog from './RoadmapPrintDialog';

type MilestoneGroupMap = {
  [groupId: number]: MilestoneGroup;
};

function addGroupsToGroupMap(grpMap: MilestoneGroupMap, msGroups: MilestoneGroup[]) {
  for (const grp of msGroups) {
    grpMap[grp.id] = grp;
    if (grp.subGroups != null) addGroupsToGroupMap(grpMap, grp.subGroups);
  }
}

function buildMsGroupMap(path: SuccessPath) {
  const msGroupPathMap: MilestoneGroupMap = {};
  if (path.milestoneGroups) addGroupsToGroupMap(msGroupPathMap, path.milestoneGroups);

  return msGroupPathMap;
}

function addGroupsToParentGroupMap(grpMap: MilestoneGroupMap, msGroup: MilestoneGroup) {
  if (msGroup.subGroups) {
    for (const child of msGroup.subGroups) {
      grpMap[child.id] = msGroup;
      if (child.subGroups) addGroupsToParentGroupMap(grpMap, child);
    }
  }
}

function buildParentMsGroupMap(path: SuccessPath) {
  const msGroupPathMap: MilestoneGroupMap = {};
  if (path.milestoneGroups) {
    for (const group of path.milestoneGroups) {
      addGroupsToParentGroupMap(msGroupPathMap, group);
    }
  }

  return msGroupPathMap;
}

function sort(grps: RoadmapMilestoneGroup[]) {
  return grps.slice().sort((g1, g2) => (g1.sortIndex ?? 0) - (g2.sortIndex ?? 0));
}

const sortMilestones = (rmMilestones: RoadmapMilestone[], milestones: Milestone[]) =>
  rmMilestones.sort((rm1, rm2) => milestones.findIndex(ms => ms.id === rm1.milestoneId) - milestones.findIndex(ms => ms.id === rm2.milestoneId));

type GrpHeaderProps = {
  msg: RoadmapMilestoneGroup;
  msGrp: MilestoneGroup;
  msGrpMap: MilestoneGroupMap;
  dragProvided: DraggableProvided;
  user: User;
};

const MilestoneGroupHeader: React.FC<GrpHeaderProps> = ({ msg, msGrp, msGrpMap, dragProvided, user }) => {
  const dlgCtrl = useDialogController(false);
  const [addMsToPath] = useAddMilestoneToPath();

  let path = `${msGrp.name?.text ?? ''}`;
  let parent = msGrp;

  while ((parent = msGrpMap[parent.id])) path = `${parent.name?.text ?? ''} / ${path}`;

  const addDefaultMilestone = (msg: MilestoneGroup) => {
    if (msg.defaultMilestoneId) void addMsToPath({ variables: { input: { milestoneGroupId: msg.id, milestoneId: msg.defaultMilestoneId, userId: user.id } } });
  };

  return (
    <CardHeader
      avatar={
        <Tooltip title={path}>
          <Icon>info_outline</Icon>
        </Tooltip>
      }
      action={
        <Grid container alignItems="center">
          {msGrp.defaultMilestoneId &&
            <IconButton role="button" aria-label="Add another milestone" aria-hidden={false} color="inherit" onClick={() => addDefaultMilestone(msGrp)}>
              <Icon>add</Icon>
            </IconButton>
          }
          <IconButton role="button" aria-label={`Edit ${msGrp.name}`} aria-hidden={false} color="inherit" onClick={() => dlgCtrl.controller.setOpen()}>
            <Icon>edit</Icon>
          </IconButton>
          <Icon  role="button" aria-label={`Reorganize ${msGrp.name}`} aria-hidden={false} {...dragProvided.dragHandleProps}>drag_indicator</Icon>
        </Grid>
      }
      title={<Goal goalItem={msg} lsData={msGrp.name} dlgCtrl={dlgCtrl} gp="goal" tp="translation" placeholder="GROUP_GOAL" />}
      style={{ backgroundColor: msGrp.color }}
    />
  );
};

const FinalizeSection: React.FC<{ roadmap: Roadmap }> = ({ roadmap }) => {
  const { controller, props } = useDialogController(false);

  return (
    <Grid item>
      <Button onClick={() => controller.setOpen()}>Finalize</Button>
      {props.open && <FinalizeRoadmap roadmap={roadmap} dlgCtrl={{ controller, props }} />}
    </Grid>
  );
};

const UserRoadmapLoader: React.FC<{ student: User; user: User; path: SuccessPath }> = ({student, user, path }) =>{
  const { data } = useRoadmap({ variables: { userId: student.id }, fetchPolicy: 'cache-first' });
  const { data: msdata } = useRoadmapMilestones({ variables: { userId: student.id }, fetchPolicy: 'cache-first' });

  if (data?.roadmap && data.roadmap.milestoneGroups && msdata?.user.roadmapMilestones) {
    const roadmap = data.roadmap;
    const msGroupMap = buildMsGroupMap(path);
    const hasMissingGroups = roadmap.milestoneGroups?.some(rmg => msGroupMap[rmg.milestoneGroupId!] == null);

    return <UserRoadmap student={student} user={user} path={path} addRmGroups={hasMissingGroups} roadmap={data.roadmap} roadmapMilestones={msdata.user.roadmapMilestones} msGroupMap={msGroupMap} />;
  }

  return <CircularProgress color="primary" />;
};

type UserRoadmapArgs = {
  student: User;
  user: User;
  path: SuccessPath;
  addRmGroups?: boolean;
  roadmap: Roadmap;
  roadmapMilestones: Milestone[];
  msGroupMap: MilestoneGroupMap;
};

const UserRoadmap: React.FC<UserRoadmapArgs> = React.memo(({ student, user, path, addRmGroups, roadmap, roadmapMilestones, msGroupMap }) => {
  const siteCtx = useContext(SiteContext);
  const [updateGroupIndecies] = useUpdateRoadmapMilestoneGroups();
  const [updateRmMilestones] = useUpdateRoadmapMilestones();
  const [printModalOpen, setPrintModalOpen] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const showAdvisorItems = user.id !== student.id && user.roles?.includes(Role.ADVISOR);
  const { taskDetailParam } = useParams<{ taskDetailParam?: string }>();
  const { data: msgdata } = addRmGroups ? useRoadmapMilestoneGroups({ variables: { userId: student.id }, fetchPolicy: 'cache-first' }) : { data: undefined };
  const taskDetail = taskDetailParam != null;

  const handleDragEnd = useCallback(
    (result: DropResult) => {
      console.log(`DragEnd`);
      const msGroups = roadmap.milestoneGroups?.slice().sort((g1, g2) => (g1.sortIndex ?? 0) - (g2.sortIndex ?? 0)).map(mg => ({id: mg.id, goal: mg.goal, sortIndex: mg.sortIndex}));
      console.log(`groups`, msGroups);
      if (msGroups == null) return;

      if (result.reason === 'DROP' && result.destination != null) {
        const sourceIndex = result.source.index;
        const destIndex = result.destination.index;
        const dir = Math.sign(destIndex - sourceIndex);
        const [start, end] = dir > 0 ? [sourceIndex, destIndex] : [destIndex, sourceIndex];
        const rmsGrps = sort(msGroups)
          .slice(start, end + 1)
          .map(rmg => ({ id: rmg.id, goal: rmg.goal, sortIndex: rmg.sortIndex }));
        const minIndex = Math.min(sourceIndex, destIndex);
        rmsGrps[sourceIndex - minIndex].sortIndex = msGroups[destIndex].sortIndex;
        for (let index = destIndex; index !== sourceIndex; index -= dir) {
          rmsGrps[index - minIndex].sortIndex = (msGroups[index].sortIndex ?? 0) - dir;
        }

        console.log(`Calling update`);

        void updateGroupIndecies({
          variables: { input: rmsGrps },
          optimisticResponse: { updateRoadmapMilestoneGroups: rmsGrps.map(grp => ({ ...grp, __typename: 'RoadmapMilestoneGroup' })) }
        });

        console.log(`Finished update`);
      }
    },
    [roadmap, updateGroupIndecies]
  );

  const toggleEmailDialog = () => setEmailModalOpen(!emailModalOpen);

  const handleUpdateRmMsClick = async (rms: RoadmapMilestone, status: RoadmapMilestoneState) => {
    await updateRmMilestones({ variables: { input: [{ id: rms.id, status }] } });
  };

  const togglePrintModal = () => {
    setPrintModalOpen(!printModalOpen);
  };

  const rmButtons = (rmMs: RoadmapMilestone) => {
    if (!showAdvisorItems) return null;

    const msStatus = UserHelper.milestoneStatus(student, rmMs.milestoneId, rmMs.id);
    if (rmMs != null) {
      if (rmMs.status == null) {
        rmMs.status =
          msStatus === MilestoneState.COMPLETE
            ? RoadmapMilestoneState.COMPLETED
            : msStatus === MilestoneState.STARTED
              ? RoadmapMilestoneState.CONTINUE
              : RoadmapMilestoneState.DEFERRED;
      }

      return <>
        <Tooltip key="completed" title={<LanguageString groupName="ROADMAP" resourceName="COMPLETED" defaultText="Completed" />}>
          <IconButton edge="end" onClick={() => handleUpdateRmMsClick(rmMs, RoadmapMilestoneState.COMPLETED)}>
            <Icon style={rmMs.status === RoadmapMilestoneState.COMPLETED ? { color: 'green' } : undefined}>thumb_up</Icon>
          </IconButton>
        </Tooltip>
        <Tooltip key="continue" title={<LanguageString groupName="ROADMAP" resourceName="CONTINUE" defaultText="Continue" />}>
          <IconButton edge="end" onClick={() => handleUpdateRmMsClick(rmMs, RoadmapMilestoneState.CONTINUE)}>
            <Icon style={rmMs.status === RoadmapMilestoneState.CONTINUE ? { color: '#f1c40f' } : undefined}>play_arrow</Icon>
          </IconButton>
        </Tooltip>
        <Tooltip key="deferred" title={<LanguageString groupName="ROADMAP" resourceName="DEFERRED" defaultText="Deferred" />}>
          <IconButton edge="end" onClick={() => handleUpdateRmMsClick(rmMs, RoadmapMilestoneState.DEFERRED)}>
            <Icon style={rmMs.status === RoadmapMilestoneState.DEFERRED ? { color: 'red' } : undefined}>stop</Icon>
          </IconButton>
        </Tooltip>
      </>;
    } else {
      return null;
    }
  };

  const createErrorMilestone = (rms: RoadmapMilestone): UserMilestone => ({
    milestone: { id: -1 },
    rmMilestone: { id: rms.id, milestoneId: -1 },
    tasks: [createErrorAssignment({ id: -1 }, { id: -1 })]
  });

  const createErrorAssignment = (ms: Milestone, t: NonNullable<Milestone['tasks']>[number]): UserMilestone['tasks'][number] => ({
    task: t,
    assignment: {
      attachments: [],
      id: -1,
      milestoneId: -1,
      needsApproval: false,
      percentComplete: 0,
      taskId: -1
    }
  });

  if (roadmap && roadmap.milestoneGroups && roadmapMilestones) {
    const parentGroupMap = buildParentMsGroupMap(path);
    if (msgdata?.user.roadmapMilestoneGroups != null) {
      for (const missingGrps of msgdata.user.roadmapMilestoneGroups) {
        if (msGroupMap[missingGrps.id] == null) msGroupMap[missingGrps.id] = missingGrps;
      }
    }

    return (
      <>
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="roadmap-list">
            {(provided, snapshot) => (
              <Grid container ref={provided.innerRef} direction="column" className="roadmap" spacing={2}>
                <Grid item xs={12}>
                  <Box bgcolor="primary.light" color="primary.contrastText">
                    <Toolbar>
                      <Typography style={{ flexGrow: 1 }}>
                        <LanguageString groupName="ROADMAP" resourceName="ROADMAP" />
                      </Typography>
                      <Tooltip title={<LanguageString groupName="ROADMAP" resourceName="EMAIL" defaultText="Send an Email" />}>
                        <IconButton onClick={toggleEmailDialog} role="button" aria-label="Send Email" color="inherit">
                          <Icon>email</Icon>
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={<LanguageString groupName="ROADMAP" resourceName="PRINT" defaultText="Print" />}>
                        <IconButton onClick={togglePrintModal} role="button" aria-label="Print" color="inherit" edge="end">
                          <Icon>print</Icon>
                        </IconButton>
                      </Tooltip>
                    </Toolbar>
                  </Box>
                </Grid>
                {sort(roadmap.milestoneGroups ?? []).map((msg, index) => {
                  const tmpGrp = msGroupMap[msg.milestoneGroupId ?? 0];
                  if (!tmpGrp) return null;

                  const msGrpId = msg.milestoneGroupId ?? 0;

                  const userMilestones = msg.milestones ? sortMilestones(msg.milestones?.slice(), tmpGrp.milestones ?? []).map(rms => {
                    const ms = tmpGrp.milestones?.find(ms => ms.id === rms.milestoneId) ?? roadmapMilestones?.find(m => m.id === rms.milestoneId);
                    if (ms == null) return createErrorMilestone(rms);

                    const tasks = ms?.tasks?.map(t => {
                      const assignment = student.assignments?.assignments?.find(a => a.roadmapMilestoneId === rms.id && a.taskId === t.id);
                      if (assignment == null) return createErrorAssignment(ms, t);
                      const ut = {
                        task: t,
                        assignment
                      };

                      return ut;
                    });
                    if (tasks == null) throw new Error('no tasks');

                    const ums = {
                      milestone: ms,
                      rmMilestone: rms,
                      tasks
                    };

                    return ums;
                  }).filter(ms => ms.milestone.id !== -1) : undefined;

                  if (userMilestones == null) throw new Error('no user milestone');

                  return (
                    <Draggable key={msGrpId} draggableId={String(msGrpId)} index={index}>
                      {(dragProvided, dragSnapshot) => (
                        <Grid key={msGrpId} item xs={12} {...dragProvided.draggableProps} ref={dragProvided.innerRef}>
                          <Card>
                            <MilestoneGroupHeader msGrp={tmpGrp} msGrpMap={parentGroupMap} msg={msg} dragProvided={dragProvided} user={student} />
                            <MilestoneList userMilestones={userMilestones} taskDetail={taskDetail} buttons={rmButtons} />
                          </Card>
                        </Grid>
                      )}
                    </Draggable>
                  );
                })}
                {siteCtx.site?.mtsEnabled && siteCtx.site?.mtsInRoadmapEnabled && <MTSInRoadmap />}
                {showAdvisorItems && <FinalizeSection roadmap={roadmap} />}
                {provided.placeholder}

                <EmailDialog open={emailModalOpen} toggle={toggleEmailDialog} />
              </Grid>
            )}
          </Droppable>
        </DragDropContext>
        <RoadmapPrintDialog open={printModalOpen} toggle={togglePrintModal} />
        <ChildRoutes />
      </>
    );
  }

  return <div>UserRoadmap</div>;
});

export const RoadmapView: React.FC = () => {
  const studentCtx = useContext(StudentContext);
  const userCtx = useContext(UserContext);

  return studentCtx?.student && studentCtx.path && userCtx.user ? (
    <UserRoadmapLoader student={studentCtx.student} user={userCtx.user} path={studentCtx.path} />
  ) : (
    <CircularProgress color="primary" />
  );
};

export default RoadmapView;
